// Libs
import classNames from 'classnames/bind';
import { useState } from 'react';
import {
  Avatar,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid2,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { yupResolver } from '@hookform/resolvers/yup';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
// Components, Layouts, Pages
import { BaseDatePicker, BaseInputAddress, Loading } from '~/components/common';
import { CloneQuoteOption } from '~/components/module/quotes';
// Others
import { useAppDispatch } from '~/utils/redux/hooks';
import { defaultValues, schema } from './helper';
import { ICloneQuote } from '~/utils/interface/quotes';
import { cloneQuote } from '~/thunks/quote/quoteThunk';
import { StorageEnum } from '~/utils/enum/base';
import { RoleEnum } from '~/utils/enum/common';
import { adminRouteAbsolute } from '~/utils/routes/roleRoutes/admin';
import { crewRouteAbsolute } from '~/utils/routes/roleRoutes/crew';
import { loaderRouteAbsolute } from '~/utils/routes/roleRoutes/loader';
import { foremanRouteAbsolute } from '~/utils/routes/roleRoutes/foreman';
import { salesAdminRouteAbsolute } from '~/utils/routes/roleRoutes/salesAdmin';
import { salesPersonRouteAbsolute } from '~/utils/routes/roleRoutes/salesPerson';
import { warehouseLeadRouteAbsolute } from '~/utils/routes/roleRoutes/warehouseLead';
import { warehouseStaffRouteAbsolute } from '~/utils/routes/roleRoutes/warehouseStaff';
import { operationsManagerRouteAbsolute } from '~/utils/routes/roleRoutes/operationsManager';
// Styles, images, icons
import styles from './CloneQuoteForm.module.scss';
import { icons } from '~/assets';

type Props = {
  onClose: () => void;
};

const cx = classNames.bind(styles);

const CloneQuoteForm = (props: Props) => {
  //#region Destructuring Props
  const { onClose } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/quotes', 'common/base']);
  const { id: quoteId } = useParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const {
    control,
    setValue,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ICloneQuote>({
    resolver: yupResolver(schema(t)),
    defaultValues: defaultValues,
  });
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const currentRole = JSON.parse(localStorage.getItem(StorageEnum.ROLE)!)[0];
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  //#endregion Implement Hook

  //#region Handle Function
  const handleCloseModal = () => {
    onClose && onClose();
    reset();
  };

  const handleCloneQuote = (data: ICloneQuote) => {
    setIsLoading(true);

    const body: ICloneQuote = {
      ...data,
      quoteId: quoteId,
    };

    dispatch(cloneQuote(body))
      .unwrap()
      .then(() => {
        setIsLoading(false);
        onClose && onClose();
        handleNavigateToQuote();
      })
      .catch(() => {
        setIsLoading(false);
      });
  };

  const handleNavigateToQuote = () => {
    switch (currentRole) {
      case RoleEnum.SUPER_ADMIN:
        navigate(adminRouteAbsolute.quotes);
        break;
      case RoleEnum.SALES_ADMIN:
        navigate(salesAdminRouteAbsolute.quotes);
        break;
      case RoleEnum.SALES_PERSON:
        navigate(salesPersonRouteAbsolute.quotes);
        break;
      case RoleEnum.OPERATIONS_MANAGER:
        navigate(operationsManagerRouteAbsolute.quotes);
        break;
      case RoleEnum.WAREHOUSE_LEAD:
        navigate(warehouseLeadRouteAbsolute.quotes);
        break;
      case RoleEnum.WAREHOUSE_STAFF:
        navigate(warehouseStaffRouteAbsolute.quotes);
        break;
      case RoleEnum.FOREMAN:
        navigate(foremanRouteAbsolute.quotes);
        break;
      case RoleEnum.CREW:
        navigate(crewRouteAbsolute.quotes);
        break;
      case RoleEnum.LOADER:
        navigate(loaderRouteAbsolute.quotes);
        break;
    }
  };
  //#endregion Handle Function

  return (
    <Dialog open={true} onClose={handleCloseModal} fullWidth maxWidth={'sm'}>
      <DialogTitle className={cx('header')}>
        <Stack direction={'column'} spacing={'3px'}>
          <Typography className={cx('title')}>{t('clone_quote_title')}</Typography>

          <Typography className={cx('description')}>{t('clone_quote_description')}</Typography>
        </Stack>

        <IconButton onClick={handleCloseModal} className={cx('buttonClose')}>
          <Avatar
            src={icons.commonIconCancel}
            className={cx('iconClose')}
            alt={t('common_alt_img', { ns: 'common/base' })}
          />
        </IconButton>
      </DialogTitle>

      <DialogContent className={cx('content')} dividers>
        <Grid2 container rowSpacing={4} columnSpacing={5}>
          <Grid2 size={6}>
            <Controller
              control={control}
              name='startDate'
              render={({ field: { value, onChange } }) => (
                <BaseDatePicker
                  mode='date'
                  label={t('clone_quote_start_date_label')}
                  value={value}
                  height={43}
                  onChange={onChange}
                  errorMessage={errors.startDate?.message}
                  required
                />
              )}
            />
          </Grid2>

          <Grid2 size={6}>
            <Controller
              control={control}
              name='startTime'
              render={({ field: { value, onChange } }) => (
                <BaseDatePicker
                  mode='time'
                  label={t('clone_quote_start_time_label')}
                  height={43}
                  value={value}
                  onChange={onChange}
                  errorMessage={errors.startTime?.message}
                  required
                />
              )}
            />
          </Grid2>

          <Grid2 size={6}>
            <Controller
              control={control}
              name='endDate'
              render={({ field: { value, onChange } }) => (
                <BaseDatePicker
                  mode='date'
                  height={43}
                  label={t('clone_quote_end_date_label')}
                  value={value}
                  onChange={onChange}
                  errorMessage={errors.endDate?.message}
                  required
                />
              )}
            />
          </Grid2>

          <Grid2 size={6}>
            <Controller
              control={control}
              name='endTime'
              render={({ field: { value, onChange } }) => (
                <BaseDatePicker
                  height={43}
                  mode='time'
                  label={t('clone_quote_end_time_label')}
                  value={value}
                  onChange={onChange}
                  errorMessage={errors.endTime?.message}
                  required
                />
              )}
            />
          </Grid2>

          {/* <Grid2 size={12}>
            <Controller
              control={control}
              name={'deliveryAddress'}
              render={({ field: { onChange, value } }) => {
                return (
                  <BaseInputAddress
                    label={t('clone_quote_delivery_address_label')}
                    value={value.address}
                    onChange={(value) => {
                      setValue('deliveryAddress.address', value, { shouldDirty: true, shouldValidate: true });
                    }}
                    height={43}
                    onSelectAddress={onChange}
                    errorMessage={errors?.deliveryAddress?.address?.message}
                    required
                  />
                );
              }}
            />
          </Grid2> */}

          <Grid2 size={12} mt={'14px'}>
            <Controller
              control={control}
              name={'isKeepPricing'}
              render={({ field: { onChange, value } }) => {
                return <CloneQuoteOption label={t('clone_quote_pricing')} isChecked={value} onChange={onChange} />;
              }}
            />
          </Grid2>

          <Grid2 size={12}>
            <Controller
              control={control}
              name={'isApplyPreviousCoupon'}
              render={({ field: { onChange, value } }) => {
                return <CloneQuoteOption label={t('clone_quote_coupon')} isChecked={value} onChange={onChange} />;
              }}
            />
          </Grid2>

          <Grid2 size={12}>
            <Controller
              control={control}
              name={'isKeepDiscountAmount'}
              render={({ field: { onChange, value } }) => {
                return <CloneQuoteOption label={t('clone_quote_discount')} isChecked={value} onChange={onChange} />;
              }}
            />
          </Grid2>

          <Grid2 size={12}>
            <Controller
              control={control}
              name={'isKeepChargingDays'}
              render={({ field: { onChange, value } }) => {
                return (
                  <CloneQuoteOption label={t('clone_quote_charging_days')} isChecked={value} onChange={onChange} />
                );
              }}
            />
          </Grid2>

          <Grid2 size={12}>
            <Controller
              control={control}
              name={'isCopyFiles'}
              render={({ field: { onChange, value } }) => {
                return <CloneQuoteOption label={t('clone_quote_files')} isChecked={value} onChange={onChange} />;
              }}
            />
          </Grid2>

          <Grid2 size={12}>
            <Controller
              control={control}
              name={'isCopyNotesAndComments'}
              render={({ field: { onChange, value } }) => {
                return <CloneQuoteOption label={t('clone_quote_notes')} isChecked={value} onChange={onChange} />;
              }}
            />
          </Grid2>
        </Grid2>
      </DialogContent>

      <DialogActions className={cx('groupButton')}>
        <Button variant='outlined' className={cx('buttonCancel')} onClick={handleCloseModal}>
          {t('common_action_cancel', { ns: 'common/base' })}
        </Button>

        <Button variant='contained' className={cx('buttonSave')} onClick={handleSubmit(handleCloneQuote)}>
          {t('common_action_save', { ns: 'common/base' })}
        </Button>
      </DialogActions>

      {isLoading && <Loading />}
    </Dialog>
  );
};

export default CloneQuoteForm;
